import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Home", href: "#home", testId: "nav-link-home" },
  { label: "Services", href: "#services", testId: "nav-link-services" },
  { label: "Data Security", href: "#data-security", testId: "nav-link-data-security" },
  { label: "Why Us", href: "#why-us", testId: "nav-link-why-us" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 border-b backdrop-blur-xl ${
        scrolled
          ? "bg-white/80 border-slate-200/70 shadow-[0_8px_30px_rgba(4,10,7,0.06)]"
          : "bg-navy/40 border-white/10"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <a
          href="#home"
          onClick={(e) => go(e, "#home")}
          className="group flex items-center gap-3"
          data-testid="nav-logo"
        >
          <img
            src="/images/revanta-mark.png"
            alt="Revanta Asset Management"
            className="h-9 w-auto transition-transform duration-300 group-hover:rotate-6"
          />
          <span
            className={`font-display text-sm font-bold uppercase tracking-[0.25em] transition-colors duration-500 ${
              scrolled ? "text-navy" : "text-white"
            }`}
          >
            Revanta
          </span>
        </a>

        <nav className="hidden items-center gap-8 md:flex" data-testid="nav-links-desktop">
          {LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => go(e, link.href)}
              data-testid={link.testId}
              className={`relative text-sm font-medium tracking-wide transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-eco after:transition-[width] after:duration-300 hover:after:w-full ${
                scrolled ? "text-slate-700 hover:text-navy" : "text-slate-200 hover:text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={(e) => go(e, "#book")}
            data-testid="nav-book-pickup-button"
            className="hidden items-center gap-2 rounded-full bg-eco px-5 py-2.5 text-sm font-bold text-navy shadow-[0_4px_20px_rgba(52,199,123,0.35)] transition-[background-color,box-shadow] duration-300 hover:bg-eco-bright hover:shadow-[0_4px_28px_rgba(52,199,123,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eco md:inline-flex"
          >
            Book a Pickup
            <ArrowRight className="h-4 w-4" />
          </motion.button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
            className={`md:hidden ${scrolled ? "text-navy" : "text-white"}`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-xl md:hidden"
            data-testid="nav-links-mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => go(e, link.href)}
                  data-testid={`${link.testId}-mobile`}
                  className="rounded-lg px-3 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-navy"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={(e) => go(e, "#book")}
                data-testid="nav-book-pickup-button-mobile"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-eco px-5 py-3 text-sm font-bold text-navy"
              >
                Book a Pickup
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
